import { create } from "zustand"

import { unlockCollection } from "@/services/core"
import type { Dialogue, GuideConfig, GuideStep, RegionId } from "@/types"
import { requireLogin, useGlobalStore } from "./global"
import { useMapStore } from "./map"
import { useTransitionStore } from "./transition"

export type GuidePhase = "idle" | "prologue" | "tutorial" | "epilogue"

interface GuideState {
    guidePhase: GuidePhase

    prologueDialogues: Dialogue[]
    prologueIndex: number
    epilogueDialogues: Dialogue[]
    epilogueIndex: number

    currentStep: number
    steps: GuideStep[]
    dialogueIndex: number

    skipped: boolean

    initGuide: (config: GuideConfig) => void
    replayGuide: (config: GuideConfig) => void
    advancePrologue: () => void
    startEpilogue: (dialogues: Dialogue[]) => void
    advanceEpilogue: () => void
    advanceStepDialogue: () => boolean
    nextStep: () => void
    startTransition: (targetRegion: RegionId) => void
    skipGuide: () => void
    isGuideDone: () => boolean
}

function markGuideDone() {
    const { gameStartTs, updateCollectionGroup } = useGlobalStore.getState()
    unlockCollection(gameStartTs, "system", "guide").then((res) => {
        if (res.code === 0) {
            updateCollectionGroup("system", res.data.list)
        }
    })
}

function finishGuide({
    skipped = false,
    targetRegion,
}: {
    skipped?: boolean
    targetRegion?: RegionId
}) {
    if (!requireLogin()) return

    markGuideDone()

    if (targetRegion) {
        useTransitionStore.getState().fadeTransition(() => {
            useMapStore.getState().initMap(targetRegion)
            useGuideStore.setState({ guidePhase: "idle", skipped })
        }, "full")
        return
    }

    useGuideStore.setState({ guidePhase: "idle", skipped })
}

export const useGuideStore = create<GuideState>((set, get) => ({
    guidePhase: "idle",

    prologueDialogues: [],
    prologueIndex: 0,
    epilogueDialogues: [],
    epilogueIndex: 0,

    currentStep: 0,
    steps: [],
    dialogueIndex: 0,

    skipped: false,

    initGuide: (config) => {
        if (get().isGuideDone()) return
        set({
            guidePhase: "prologue",
            prologueDialogues: config.prologueDialogues,
            prologueIndex: 0,
            epilogueDialogues: config.epilogueDialogues ?? [],
            epilogueIndex: 0,
            steps: config.steps,
            currentStep: 0,
            dialogueIndex: 0,
            skipped: false,
        })
    },

    replayGuide: (config) => {
        set({
            guidePhase: "prologue",
            prologueDialogues: config.prologueDialogues,
            prologueIndex: 0,
            epilogueDialogues: config.epilogueDialogues ?? [],
            epilogueIndex: 0,
            steps: config.steps,
            currentStep: 0,
            dialogueIndex: 0,
            skipped: false,
        })
    },

    advancePrologue: () => {
        const { prologueIndex, prologueDialogues } = get()
        if (prologueIndex < prologueDialogues.length - 1) {
            set({ prologueIndex: prologueIndex + 1 })
        } else {
            useTransitionStore.getState().fadeTransition(() => {
                set({ guidePhase: "tutorial", dialogueIndex: 0 })
            }, "full")
        }
    },

    startEpilogue: (dialogues) => {
        set({
            guidePhase: "epilogue",
            epilogueDialogues: dialogues,
            epilogueIndex: 0,
        })
    },

    advanceEpilogue: () => {
        const { epilogueIndex, epilogueDialogues } = get()
        if (epilogueIndex < epilogueDialogues.length - 1) {
            set({ epilogueIndex: epilogueIndex + 1 })
        } else {
            finishGuide({})
        }
    },

    advanceStepDialogue: () => {
        const { currentStep, steps, dialogueIndex } = get()
        const step = steps[currentStep]
        if (!step) return false
        if (dialogueIndex < step.dialogues.length - 1) {
            set({ dialogueIndex: dialogueIndex + 1 })
            return false
        }
        return true
    },

    nextStep: () => {
        const { currentStep, steps, epilogueDialogues } = get()
        if (currentStep < steps.length - 1) {
            set({ currentStep: currentStep + 1, dialogueIndex: 0 })
        } else if (epilogueDialogues.length > 0) {
            set({
                guidePhase: "epilogue",
                epilogueIndex: 0,
            })
        } else {
            finishGuide({})
        }
    },

    startTransition: (targetRegion: RegionId) => {
        finishGuide({ targetRegion })
    },

    skipGuide: () => {
        if (get().guidePhase === "epilogue") {
            finishGuide({ skipped: true })
            return
        }

        const steps = get().steps
        const lastStep = steps[steps.length - 1]
        const targetRegion = lastStep?.onComplete?.withTransition
            ? lastStep.onComplete.switchRegion
            : undefined

        finishGuide({ skipped: true, targetRegion })
    },

    isGuideDone: () => {
        const player = useGlobalStore.getState().player
        if (!player) return true
        const systemCollection = player.collection?.system
        return systemCollection?.some((item) => item.key === "guide") ?? false
    },
}))
